import { Entry, Locale } from 'contentful';
import hash from 'object-hash';
import IPresentationMaster from '@app/types/presentations/master/IPresentationMaster';
import { TypePresentationMasterSkeleton } from '@/@types/generated/contentful';
import AbstractPresentationMasterService from './AbstractPresentationMasterService';
import IRawPresentationMaster from '../../types/presentations/master/IRawPresentationMaster';

export default class PresentationMasterService extends AbstractPresentationMasterService {
	/**
	 * Get existing presentations from contentful
	 */
	public async getAll(): Promise<IPresentationMaster[]> {
		// const localeCodes: string[] = locales.items.map((locale: Locale): string => locale.code);
		const masters: IPresentationMaster[] = [];

		const entries = await this.deliveryClient.withAllLocales.getEntries({ content_type: 'presentationMaster', include: 10 }); // eslint-disable-line

		entries.items.forEach((entry): void => {
			const checksum: string = this.getPresentationRawMasterChecksum(entry);
			masters.push(
				...this.locales.map(
					(locale: Locale): IPresentationMaster =>
						this.mapPresentationMasterEntry(
							{
								id: entry.sys.id,
								entry: this.mapToSingleLocaleObject(entry, locale),
								checksum
							},
							locale.code
						)
				)
			);
		});

		return masters;
	}

	/**
	 * Fetch and initialize an existing presentation master from Contentful
	 *
	 * @param id
	 */
	public async get(id: string): Promise<Record<string, IPresentationMaster>> {
		// const localeCodes: string[] = locales.items.map((locale: Locale): string => locale.code);
		const mastersByLocale: Record<string, IPresentationMaster> = {};

		try {
			const entry: Entry<any> = await this.deliveryClient.withAllLocales.getEntry(id, { include: 10 });
			const checksum: string = this.getPresentationRawMasterChecksum(entry);

			this.locales.forEach((locale: Locale): void => {
				mastersByLocale[locale.code] = this.mapPresentationMasterEntry(
					{
						id: entry.sys.id,
						entry: this.mapToSingleLocaleObject(entry, locale),
						checksum
					},
					locale.code
				);
			});

			return mastersByLocale;
		} catch (error) {
			this.throwIfResourceNotFoundError(
				error,
				`Presentation master entry with id '${id}' could not be retrieved as it does not exist.`
			);
			throw error;
		}
	}

	/**
	 * Fetch and initialize an existing presentation master from Contentful
	 * Return the raw entry data
	 *
	 * @param id
	 */
	public async getRaw(id: string): Promise<IRawPresentationMaster<'WITH_ALL_LOCALES'>> {
		try {
			const entry = await this.deliveryClient.withAllLocales.getEntry<TypePresentationMasterSkeleton>(id, { include: 10 });
			console.log('entry', entry);
			return {
				id,
				entry,
				checksum: this.getPresentationRawMasterChecksum(entry)
			};
		} catch (error) {
			this.throwIfResourceNotFoundError(
				error,
				`Presentation master entry with id '${id}' could not be retrieved as it does not exist.`
			);
			throw error;
		}
	}

	/**
	 * Build a checksum over a given multi-locale (fetched with locale: *) presentation master Entry
	 * @param multiLocaleEntry
	 */
	private getPresentationRawMasterChecksum(multiLocaleEntry: Entry<any>): string {
		return hash(
			JSON.stringify({
				...multiLocaleEntry,
				sys: null,
				fields: {
					...multiLocaleEntry.fields,
					changelog: []
				}
			})
		).substring(0, 8);
	}
}
